import {
  IntlProvider as RIntlProvider,
  LocalizationProvider as RLocalizationProvider,
  useIntl,
} from '@faceup/localization'
import { convertBasenameToLanguage, type Language, languageEnumToBasename } from '@faceup/utils'
import { type ReactNode, useContext, useEffect, useState } from 'react'
import { sharedMessages } from '../Shared/translations'
import { useCustomApollo } from './CustomApolloProvider'
import { LanguageContext } from './LanguageContext'
import { LanguageProvider } from './LanguageProvider'

type LanguageWrapperProps = {
  children: ReactNode
  language: Language
}

export const LanguageWrapper = ({ children, language }: LanguageWrapperProps) => {
  // Allow the focalazy parent frame to override the viewer's language while
  // inside the click-to-edit iframe. Purely visual for the translator; the
  // resolved language stays untouched. Reset whenever the prop changes.
  const [override, setOverride] = useState<Language | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset override only when the language prop changes
  useEffect(() => {
    setOverride(null)
  }, [language])

  useEffect(() => {
    const onSetLanguage = (event: Event) => {
      const custom = event as CustomEvent<{ language: string }>
      const code = custom.detail?.language
      if (!code) {
        return
      }
      const next = convertBasenameToLanguage(code)
      if (next) {
        setOverride(next)
      }
    }
    window.addEventListener('focalazy:set-language', onSetLanguage)
    return () => window.removeEventListener('focalazy:set-language', onSetLanguage)
  }, [])

  const effectiveLanguage = override ?? language

  return (
    <LanguageProvider language={effectiveLanguage}>
      <IntlProvider>
        <LocalizationProvider language={effectiveLanguage}>{children}</LocalizationProvider>
      </IntlProvider>
    </LanguageProvider>
  )
}

const IntlProvider = ({ children }: { children: ReactNode }) => {
  const languageContext = useContext(LanguageContext)

  // No key={language}: react-intl handles locale/messages prop changes without a
  // remount.
  return (
    <RIntlProvider
      locale={languageEnumToBasename(languageContext.language)}
      messages={languageContext.messages}
      onError={() => null}
    >
      {children}
    </RIntlProvider>
  )
}

const LocalizationProvider = ({
  children,
  language,
}: {
  children: ReactNode
  language: Language
}) => {
  const { setApiErrorMessage } = useCustomApollo()
  const { formatMessage } = useIntl()
  // Sync the Apollo error-notification copy to this language. Do NOT depend on the
  // current apiErrorMessage: with a base (router-level) and a per-branch language
  // layer mounted at once, depending on the shared value makes the two layers
  // fight over it forever (each re-sets it in its own language), re-rendering the
  // whole tree on a loop. setState bails on an unchanged value, so a plain set is
  // safe. The innermost (active content) language usually wins, though on a synchronous
  // co-update the write order isn't guaranteed - it's a transient toast copy, so any
  // brief mismatch self-corrects on the next render.
  useEffect(() => {
    setApiErrorMessage(formatMessage(sharedMessages.apiError))
  }, [formatMessage, setApiErrorMessage])
  return <RLocalizationProvider language={language}>{children}</RLocalizationProvider>
}
